/**
 * What the loan tape page's Analyst rail sends with every turn.
 *
 * A *descriptor*, never data: only `tape_id` and `run_id` identify anything,
 * and the server re-reads that tape's thread owner-scoped at turn time. The
 * tab, the Mapping view and the open column say where on the page the question
 * was asked, so "this column" and "this run" mean what is on screen.
 */

import type { LoanTapeRun } from "../../services/loanTapeDashboardService";

export interface LoanTapeContext {
	kind: "loan_tape_run";
	/** The tape: the id of the `loan_tape` thread it is made of. */
	tape_id: string;
	/** The run on screen. Null means the newest, which the page shows by default. */
	run_id: number | null;
	/** The page tab the question was asked from, so the thread reopens there. */
	tab: string | null;
	/** Columns, Map or SQL, when the Mapping tab is the one on screen. */
	mapping_view: string | null;
	/** The column open in the side panel, so "this column" means one of them. */
	column: string | null;
}

export interface LoanTapeContextInput {
	tapeId: string;
	/** The run the page is showing, when it has one. */
	run: LoanTapeRun | null;
	tab?: string | null;
	mappingView?: string | null;
	column?: string | null;
}

export function buildLoanTapeContext({
	tapeId,
	run,
	tab = null,
	mappingView = null,
	column = null,
}: LoanTapeContextInput): LoanTapeContext {
	return {
		kind: "loan_tape_run",
		tape_id: tapeId,
		run_id: run?.id ?? null,
		tab,
		mapping_view: tab === "mapping" ? mappingView : null,
		column,
	};
}

/**
 * The rail's context chip: what "this" means in every question sent.
 *
 * The tape's name is the page heading, so the chip spends its width on what
 * the heading does not say: which run is on screen, and the column that is
 * selected. `startedLabel` dates an earlier run, since "earlier" alone could
 * be any of them.
 */
export function loanTapeChipLabel({
	run,
	isLatest,
	startedLabel = null,
	column = null,
}: {
	run: LoanTapeRun | null;
	isLatest: boolean;
	startedLabel?: string | null;
	column?: string | null;
}): string {
	if (!run) return "Not built yet";
	const which = isLatest
		? "Latest run"
		: `Earlier run${startedLabel ? ` (${startedLabel})` : ""}`;
	return column ? `${which} · ${column}` : which;
}
